import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import { optionalUrlSchema } from '@/lib/utils/url-schema.utils';
import { optionalWholeNumber } from '@/lib/utils/whole-number-schema.utils';
import {
  MAX_DESCRIPTION_LENGTH,
  MAX_FOUNDING_TEAM,
  MAX_SECTOR_OTHER_LENGTH,
  MAX_SUBSECTORS,
  RAISE_RANGE_ERROR,
  SECTOR_OTHER_LENGTH_ERROR,
  SELF_ID_ANSWERS,
} from './constants';

/**
 * Presence-check relaxation (MYN-127/131, client decision 2026-08-05):
 * `docs/reference/data-fields.md`'s Startup Profile Model table marks most
 * fields "Req", but the table's own preamble is explicit that this means
 * "required for a complete/matchable profile" for the Capital Raise matching
 * algorithm - NOT required to submit/save the profile ("All fields optional
 * for Startup Admins"). This schema previously enforced those "Req" markers
 * as submit-time blocks (`.min(1, '...is required')` / a required-only
 * transform), which was a frontend-only divergence from that dictionary.
 *
 * Every field below except `companyName` has had its presence check removed
 * as a result - `companyName` stays required for reasons unrelated to
 * data-fields.md (duplicate-domain detection, FR-13, and it's displayed on
 * other screens). FORMAT validation is preserved wherever a value IS
 * supplied: a URL must still look like a URL, a number must still parse and
 * be non-negative, an enum value must still be a member of its set. Optional
 * means "may be empty", not "accepts garbage".
 */

/**
 * Optional URL: blank is allowed; a bare host ("www.example.com") is accepted
 * and saved with `https://` prepended; anything else must be a valid URL.
 */
const optionalUrl = optionalUrlSchema;

/**
 * Optional string array from a checkbox group. React Hook Form yields `false`
 * (not `[]`) when a multi-checkbox group has nothing selected, so coerce any
 * non-array to undefined before validating.
 */
const optionalStringArray = z.preprocess(
  (value) => (Array.isArray(value) ? value : undefined),
  z.array(z.string()).optional(),
);

/** Same as `optionalStringArray`, but for a checkbox group of numeric ids (Subsector.id). */
const optionalNumberArray = z.preprocess(
  (value) => (Array.isArray(value) ? value.map(Number) : undefined),
  z.array(z.number()).max(MAX_SUBSECTORS).optional(),
);

/** Blank select/segment state ('' or null) normalized to "unanswered" before the enum check. */
function blankToUndefined(value: unknown): unknown {
  return value === '' || value === null ? undefined : value;
}

/**
 * Founder self-identification answer (UX sheet r113/r114). Shared with the
 * startup profile editor's schema so both forms accept exactly the same values.
 */
export const selfIdAnswerSchema = z.preprocess(blankToUndefined, z.enum(SELF_ID_ANSWERS).optional());

/** Yes/No choice held as a string at the form layer (e.g. "Do you have a lead investor?"). */
export const yesNoSchema = z.preprocess(blankToUndefined, z.enum(['yes', 'no']).optional());

/** Free-text sector, only collected when the primary sector is "Other" (UX sheet r104). */
export const sectorOtherSchema = z
  .string()
  .trim()
  .max(MAX_SECTOR_OTHER_LENGTH, SECTOR_OTHER_LENGTH_ERROR)
  .optional();

export const foundingTeamMemberSchema = z.object({
  name: z.string().trim().optional(),
  title: z.string().trim().optional(),
  linkedinUrl: optionalUrl,
  // Client decision 2026-08-05: data-fields.md marks Past Exits "Req" the
  // same way it marks Name/Title "Req" - matching-only, not a submission
  // block - so it drops its presence check too; a supplied value must still
  // be 'yes'/'no'. This supersedes the 2026-07-22 note that deliberately
  // kept this one presence-checked ("no sensible default to pre-fill"):
  // leaving it required blocked the wizard's default single co-founder row
  // (which never pre-fills Past Exits, see EMPTY_MEMBER in
  // CreateStartupForm.tsx) from ever advancing past the Founding Team step
  // with nothing filled in, contradicting this same decision. RHF still
  // gives '' (not undefined) for an untouched select, so '' is normalized to
  // undefined first rather than left to fail as an invalid enum value.
  pastExits: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.enum(['yes', 'no']).optional(),
  ),
});

/**
 * Whole create-startup form (MYN-46/127/131). `companyName` is the only
 * field that blocks submission (see the file-level comment above for why);
 * every other field's presence check was removed per the 2026-08-05 client
 * decision - they remain required *inputs to the matching algorithm*, not to
 * saving a profile. Format validation (URL/number/enum shape) still applies
 * to any value that IS supplied.
 */
export const createStartupSchema = z.object({
  // Step 1 - Basic Details
  companyName: z.string().trim().min(1, 'Company name is required').max(200),
  companyWebsiteUrl: optionalUrl,
  companyLinkedinUrl: optionalUrl,
  briefDescription: z
    .string()
    .trim()
    .max(MAX_DESCRIPTION_LENGTH, `Keep it under ${MAX_DESCRIPTION_LENGTH} characters`)
    .optional(),
  // Step 1 - Sector & Stage. sectorId/subsectorIds/hqCountryId/hqStateId/
  // hqCityId are Sector/Subsector/Country/State/City ids (MYN-46 - real
  // reference-data dropdowns, not free text).
  sectorId: optionalWholeNumber,
  subsectorIds: optionalNumberArray,
  sectorOther: sectorOtherSchema,
  // Display-only (Step 4 review) - set alongside hqCityId when the Combobox
  // option is chosen (SectorStage), since City is search-backed rather than
  // a fully-fetched list like Sector/Country/State - never sent to the API.
  hqCityName: z.string().optional(),
  fundingStage: z.string().optional(),
  yearFounded: optionalWholeNumber,
  hqCountryId: optionalWholeNumber,
  hqStateId: optionalWholeNumber,
  hqCityId: optionalWholeNumber,
  marketModels: optionalStringArray,
  // Step 1 - Financials
  totalRevenueRange: z.string().optional(),
  arr: optionalWholeNumber,
  amountRaisedToDate: optionalWholeNumber,
  nondilutiveFunding: optionalWholeNumber,
  recentMilestone: z.string().trim().optional(),
  // Step 1 - Uploads & Links (S3 keys set by the upload flow)
  // `logoFileKey` is the WRITE field (the raw S3 key, the only logo field the
  // Backend's create DTO accepts - MYN-175); `logoUrl` is the READ field,
  // holding the Backend-resolved display URL for that same object. Same split
  // as the Trusted Partner profile's logoFileKey/logoUrl. Only logoFileKey is
  // sent - see to-payload.ts.
  logoFileKey: z.string().trim().optional(),
  logoUrl: z.string().optional(),
  // Pre-existing note (unrelated to the 2026-08-05 relaxation above): S3
  // presigned uploads aren't wired up in local dev
  // (FileUploadService.presign throws FILE_UPLOAD_UNAVAILABLE when
  // storage.bucket is unset - Backend/src/modules/file-upload/file-upload.service.ts),
  // which would block the whole form locally if this were presence-checked -
  // tracked in Backend/docs/decisions/mocks-and-tech-debt.md.
  pitchDeckFileKey: z.string().trim().optional(),
  pitchDeckFileSizeBytes: optionalWholeNumber,
  // A link, not an upload (A-58). Uses the same optionalUrl helper as every
  // other URL field so a typo is a field error, not a server 400. The BACKEND
  // additionally pins the scheme to http/https - that is the boundary that
  // matters, since this value lands in an href on the public shared profile.
  pitchVideoUrl: optionalUrl,
  // Step 1 - Additional details (AI Classification, Revenue Model, Growth
  // Rate, Paying Customers, Investor Types Sought) - matching-critical per
  // data-fields.md, presence-check removed per the file-level comment above.
  aiClassification: z.string().optional(),
  revenueModel: z.string().optional(),
  revenueGrowthRate: z.string().optional(),
  payingCustomers: z.string().optional(),
  investorTypesSought: optionalStringArray,
  employeeCount: optionalWholeNumber,
  useOfFunds: z.string().trim().optional(),
  // Step 2 - Founding Team
  foundingTeam: z.array(foundingTeamMemberSchema).max(MAX_FOUNDING_TEAM).optional(),
  founderFemale: selfIdAnswerSchema,
  founderUnderrepresented: selfIdAnswerSchema,
  founderUnderrepresentedCategories: optionalStringArray,
  // Step 3 - Current Raise
  financeSoughtLow: optionalWholeNumber,
  financeSoughtHigh: optionalWholeNumber,
  valuationOrCap: optionalWholeNumber,
  instrument: z.string().optional(),
  targetCloseDate: z.string().optional(),
  hasLeadInvestor: yesNoSchema,
  leadInvestorName: z.string().trim().optional(),
}).refine(
  (values) =>
    values.financeSoughtLow == null ||
    values.financeSoughtHigh == null ||
    values.financeSoughtHigh >= values.financeSoughtLow,
  {
    message: RAISE_RANGE_ERROR,
    path: ['financeSoughtHigh'],
  },
);

export type CreateStartupFormValues = z.infer<typeof createStartupSchema>;

/** Typed as the parsed shape, as resolvers v3 did; see `eventFormResolver`. */
export const createStartupResolver = zodResolver(
  createStartupSchema,
) as Resolver<CreateStartupFormValues>;
export type FoundingTeamMemberFormValues = z.infer<typeof foundingTeamMemberSchema>;

/** Field names owned by each step, used to validate progressively on Next.
 * None of these fields block Next any more (2026-08-05 - only `companyName`,
 * Step 1, blocks); they stay listed so a *malformed* supplied value (a
 * garbled URL, a non-numeric year) still surfaces before the user moves on. */
export const STEP_FIELDS: Record<number, (keyof CreateStartupFormValues)[]> = {
  0: [
    'companyName',
    'companyWebsiteUrl',
    'companyLinkedinUrl',
    'briefDescription',
    'sectorId',
    'subsectorIds',
    'sectorOther',
    'fundingStage',
    'yearFounded',
    'hqCountryId',
    'hqStateId',
    'hqCityId',
    'marketModels',
    'totalRevenueRange',
    'arr',
    'amountRaisedToDate',
    'nondilutiveFunding',
    'pitchVideoUrl',
    // pitchDeckFileKey excluded: it's a plain optional string with no format
    // check of its own, so listing it here would be a no-op trigger (unlike
    // the numeric/URL fields above, which still catch a malformed value).
    'aiClassification',
    'revenueModel',
    'revenueGrowthRate',
    'payingCustomers',
    'investorTypesSought',
  ],
  1: ['foundingTeam'],
  2: [
    'financeSoughtLow',
    'financeSoughtHigh',
    'instrument',
    'targetCloseDate',
    'valuationOrCap',
    'hasLeadInvestor',
    'leadInvestorName',
  ],
};
